import { Controller } from '@hotwired/stimulus'
import * as d3 from 'd3'
import d3Cloud from 'd3-cloud'

export default class extends Controller {
  static values = {
    data: Array,
    maxWords: { type: Number, default: 50 },
    padding: { type: Number, default: 0 },
    minFontSize: { type: Number, default: 12 },
    maxFontSize: { type: Number, default: 100 },
    width: { type: Number, default: 900 },
    height: { type: Number, default: 600 }
  }

  connect() {
    d3.select(this.element).selectAll('svg').remove()
    this.drawCloud()
  }

  disconnect() {
    d3.select(this.element).selectAll('svg').remove()
  }

  drawCloud() {
    const width = this.widthValue
    const height = this.heightValue

    const svgNode = WordCloud(this.dataValue, {
      width,
      height,
      maxWords: this.maxWordsValue,
      padding: this.paddingValue,
      minFontSize: this.minFontSizeValue,
      maxFontSize: this.maxFontSizeValue,
      useColorScale: true,
      rotate: () => {
        // Random rotation: 0° or 90° for clean layout
        const angles = [0, 90]
        return angles[Math.floor(Math.random() * angles.length)]
      }
    })

    this.element.appendChild(svgNode)
  }
}

// WordCloud function com d3.scaleSqrt e limites de fonte
function WordCloud(
  wordsArray,
  {
    size = (group) => group.length,
    width = 900,
    height = 600,
    maxWords = 75,
    minFontSize = 12,
    maxFontSize = 100,
    fill = 'currentColor',
    useColorScale = false,
    rotate = 0,
    padding = 0,
  } = {}
) {
  const words = typeof wordsArray === 'string' ? wordsArray.split(/\W+/g) : Array.from(wordsArray)

  const data = d3
    .rollups(words, size, (w) => w)
    .sort((a, b) => d3.descending(a[1], b[1]))
    .slice(0, maxWords)
    .map(([text, size]) => ({ text, size }))

  // Escala de fonte balanceada com limites configuráveis
  const fontSizeScale = d3
    .scaleSqrt()
    .domain([0, d3.max(data, (d) => d.size)])
    .range([minFontSize, maxFontSize])

  // Escala de cores Plaza3 - tons de amarelo/âmbar/laranja quente
  const colorScale = d3.scaleOrdinal()
    .domain([0, data.length])
    .range([
      '#f59e0b', // amber-500
      '#fbbf24', // amber-400
      '#fb923c', // orange-400
      '#f97316', // orange-500
      '#ea580c', // orange-600
      '#d97706', // amber-600
      '#b45309', // amber-700
      '#92400e'  // amber-800
    ])

  const svg = d3
    .create('svg')
    .attr('viewBox', [0, 0, width, height])
    .attr('preserveAspectRatio', 'xMidYMid meet')
    .attr('width', width)
    .attr('height', height)
    .attr('text-anchor', 'middle')
    .attr('style', 'max-width: 100%; height: auto;')

  const g = svg.append('g')

  const cloud = d3Cloud()
    .size([width, height])
    .words(data)
    .padding(padding)
    .rotate(rotate)
    .fontSize((d) => fontSizeScale(d.size))
    .on('end', draw)

  cloud.start()

  function draw(words) {
    svg
      .append('g')
      .attr('transform', 'translate(' + cloud.size()[0] / 2 + ',' + cloud.size()[1] / 2 + ')')
      .selectAll('text')
      .data(words)
      .enter()
      .append('text')
      .style('font-size', (d) => d.size + 'px')
      .style('fill', useColorScale ? (d, i) => colorScale(i) : fill)
      .attr('text-anchor', 'middle')
      .style('font-family', 'Impact')
      .style('font-weight', 'bold')
      .attr('transform', (d) => 'translate(' + [d.x, d.y] + ')rotate(' + d.rotate + ')')
      .text((d) => d.text)
  }

  return svg.node()
}
